import React from 'react';
import { HOME_SECTIONS } from './homeSections';
import { resolveBlock, normalizeType } from '../puck/registry';
import BlockErrorBoundary from '../puck/BlockErrorBoundary';

/**
 * Rendu de la page Puck `accueil` : chaque bloc est résolu vers
 *  1) une SECTION dynamique de l'accueil (carrousel, événements, don…) → le vrai
 *     composant app (HOME_SECTIONS), OU
 *  2) un bloc de CONTENU Puck (Hero/Bannière/Événement/Don/Plan) → rendu partagé
 *     (registre puck/registry).
 * Un type inconnu est ignoré (jamais de crash). Les clés de blocs-section sont
 * fixées ici ET côté intranet (config.js) — garder les deux alignés.
 */

// alias normalisé (minuscules, alphanum) → clé HOME_SECTIONS
const SECTION_ALIASES = {};
function aliasSection(key, aliases) {
  for (const a of aliases) SECTION_ALIASES[normalizeType(a)] = key;
}
aliasSection('banner',        ['SectionBanniere', 'BanniereSaisonniere', 'home:banner']);
aliasSection('hero',          ['SectionHero', 'HeroAccueil', 'home:hero']);
aliasSection('video',         ['SectionVideo', 'VideoDirect', 'home:video']);
aliasSection('events',        ['SectionEvenements', 'Evenements', 'home:events']);
aliasSection('reading-plans', ['SectionPlansLecture', 'PlansLecture', 'home:reading-plans']);
aliasSection('library',       ['SectionLibrairie', 'Librairie', 'home:library']);
aliasSection('giving',        ['SectionDon', 'Donner', 'home:giving']);
aliasSection('prayer',        ['SectionPriere', 'LignePriere', 'home:prayer']);
aliasSection('pastoral-rdv',  ['SectionRdv', 'RendezVousPastoral', 'home:pastoral-rdv']);
aliasSection('infos-pcc',     ['SectionInfosPCC', 'InfosPCC', 'home:infos-pcc']);
aliasSection('location',      ['SectionLocalisation', 'NousTrouver', 'home:location']);

export function resolveHomeSection(type) {
  const key = SECTION_ALIASES[normalizeType(type)];
  return key ? HOME_SECTIONS[key].Component : null;
}

// Visibilité d'une rubrique selon dates + public (étape 3.2, « Toutes »).
// REPLI CONSERVATEUR : rien n'est masqué tant qu'aucune contrainte n'est saisie.
function isBlockVisible(props, viewer) {
  const now = new Date();
  if (props.visibleFrom) {
    const from = new Date(props.visibleFrom);
    if (!isNaN(from.getTime()) && now < from) return false;
  }
  if (props.visibleUntil) {
    const until = new Date(props.visibleUntil);
    if (!isNaN(until.getTime())) { until.setHours(23, 59, 59, 999); if (now > until) return false; }
  }
  const aud = props.audience || 'all';
  if (viewer && aud !== 'all') {
    if (aud === 'visitors' && viewer.isMember) return false;
    if (aud === 'members' && !viewer.isMember) return false;
  }
  return true;
}

/** Rend un bloc de la page `accueil`. `theme` sert aux blocs de contenu. */
function renderHomeBlock(block, i, theme, viewer) {
  const type = block?.type;
  const props = block?.props || {};
  const key = props.id || `${type}-${i}`;

  if (!isBlockVisible(props, viewer)) return null;

  const Section = resolveHomeSection(type);
  if (Section) {
    // On transmet les props de la rubrique (champs éditables). Chaque section
    // applique un REPLI sur sa valeur codée actuelle quand un champ est vide →
    // l'accueil reste identique tant que rien n'est saisi (étape 3.2).
    return (
      <BlockErrorBoundary key={key} type={type}>
        <Section props={props} />
      </BlockErrorBoundary>
    );
  }
  const Content = resolveBlock(type);
  if (Content) {
    return (
      <BlockErrorBoundary key={key} type={type}>
        <Content props={props} theme={theme} />
      </BlockErrorBoundary>
    );
  }
  return null; // type inconnu → ignoré
}

/** Rend la liste des blocs de la page `accueil`.
 *  `viewer` (optionnel : { isMember }) sert au filtrage par public. Absent
 *  (aperçu CMS) → aucun filtrage par public. */
export function renderHomeContent(content, theme, viewer) {
  if (!Array.isArray(content)) return null;
  return content.map((block, i) => renderHomeBlock(block, i, theme, viewer));
}
